/**
 * Ratings — star rating display with site-specific fill colors.
 *
 * Ported from:
 * - hec-composable/hec/components/ratings/Ratings.tsx
 * - hec-composable/hec/components/ratings/Stars.tsx
 * - sfcc-website/org_hec/cartridge/templates/default/product/components/productReviews.isml
 *
 * Two site-themed fill colors, selected by `variant` (per-site values in
 * app.css):
 * - "default" → --tile-star-color (PDP / PLP / PowerReviews)
 * - "recs"    → --recs-star-color (recommendation carousels)
 * Empty star: --color-gray-04.
 *
 * Supports partial star fill (e.g., 3.7 stars fills the 4th star to 70%).
 */
import { cn } from "~/lib/utils";

import { StarIcon } from "./star-icon";

// ---------------------------------------------------------------------------
// Stars sub-component
// ---------------------------------------------------------------------------

interface StarsProps {
  filledStars: number;
  remainder: number;
  variant: "default" | "recs";
  /** Tailwind h-/w- classes for each star, derived from the caller's `size`. */
  starSizeClass: string;
}

// Star size is surface-aware via the caller's `size` prop:
// - "small" (product tiles, quick view, cart dialog, hybrid PDP): 14px — matches
//   SFRA's compact tile rating and keeps the stars + "N Reviews" label inside the
//   narrow tile (≈152px content box at a 360px viewport) instead of clipping the
//   label off the card's overflow-hidden edge.
// - "medium" / "large" (main PDP rating): 17px.
// Explicit px, not the Radix spacing scale this repo extends Tailwind with — there
// `h-6` is 32px (Radix space-6), not 24px. SFCC-11626, SFCC-11705.
const STAR_SIZE_SMALL = "h-[14px] w-[14px]";
const STAR_SIZE_DEFAULT = "h-[17px] w-[17px]";

function Stars({ filledStars, remainder, variant, starSizeClass }: StarsProps) {
  // StarIcon fills with `currentColor`, so the text-color utility sets the
  // fill. Two per-site colors (app.css): the default tile-star on PDP/PLP,
  // recs-star on recommendation carousels.
  const fullColorClass =
    variant === "recs" ? "text-recs-star" : "text-tile-star";

  return (
    <>
      {Array.from({ length: 5 }, (_, index) => {
        const isFilled = index < filledStars;
        const isPartial = index === filledStars && remainder > 0;

        return (
          <div
            key={index}
            className="xs:mx-[.07rem] relative inline-block sm:mx-[.1rem]"
          >
            {/* Empty star (always rendered as background) */}
            <StarIcon className={cn("text-gray-04", starSizeClass)} />

            {/* Full star overlay */}
            {isFilled && (
              <StarIcon
                className={cn(
                  "absolute left-0 top-0",
                  fullColorClass,
                  starSizeClass,
                )}
              />
            )}

            {/* Partial star overlay (clipped by width percentage) */}
            {isPartial && (
              <div
                className="absolute left-0 top-0 overflow-hidden"
                style={{ width: `${remainder * 100}%` }}
              >
                <StarIcon className={cn(fullColorClass, starSizeClass)} />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Ratings component
// ---------------------------------------------------------------------------

interface RatingsProps {
  rating: number;
  reviewCount?: number;
  siteId?: string;
  size?: "small" | "medium" | "large";
  /** Star fill color: "recs" on recommendation carousels, "default" elsewhere. */
  variant?: "default" | "recs";
  /**
   * When provided, the whole snippet renders as a `<button type="button">` —
   * caller wires the scroll-to-reviews behavior. Omit for non-interactive use.
   */
  onClick?: () => void;
}

export function Ratings({
  rating,
  reviewCount,
  size = "small",
  variant = "default",
  onClick,
}: RatingsProps) {
  const filledStars = Math.floor(rating);
  const remainder = rating - filledStars;
  const starSizeClass = size === "small" ? STAR_SIZE_SMALL : STAR_SIZE_DEFAULT;

  const content = (
    <>
      <div className="flex">
        <Stars
          filledStars={filledStars}
          remainder={remainder}
          variant={variant}
          starSizeClass={starSizeClass}
        />
      </div>
      <label
        className={cn(
          "xs:ml-[.2rem] ml-0 mt-[.2rem] flex shrink-0 whitespace-nowrap font-grotesque font-light",
          {
            // Tiles / dialogs: compact ~11px label (matches SFRA's tile rating)
            // so stars + "N Reviews" fit a narrow tile without clipping.
            "text-[.7rem]": size === "small",
            // Main PDP rating: .7rem on mobile → 14px on desktop.
            "text-[.7rem] sm:text-sm": size === "medium" || size === "large",
            "ml-[.45rem]": size === "small" || size === "medium",
            "ml-[.5rem]": size === "large",
          },
        )}
      >
        {reviewCount ? reviewCount : "0"} Reviews
      </label>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="flex w-full cursor-pointer flex-row items-center bg-transparent p-0 text-left"
      >
        {content}
      </button>
    );
  }

  return <div className="flex w-full flex-row items-center">{content}</div>;
}
